import geocodingFactory from '@mapbox/mapbox-sdk/services/geocoding';

const { accessToken = null } = window.MapboxConfig ?? {};
const geocodingService = geocodingFactory({ accessToken });

const searchBox = document.querySelector('.search-box');
const searchBoxInput = searchBox.querySelector('.search-box__input');
const searchResultsList = searchBox.querySelector('.search-box__results');
const clientLocationButton = searchBox.querySelector('.search-box__button');
const messageError = searchBox.querySelector('.search-box__message--error');
const messageNoResults = searchBox.querySelector('.search-box__message--no-results');
const messageConsentRequired = searchBox.querySelector('.search-box__message--consent-required');
const consentLink = searchBox.querySelector('.search-box__consent-link');

const searchBoxResultButtonClass = 'search-box__result-button';

let callbacks = [];
const addCallback = callback => callbacks.push(callback);
const executeCallbacks = (coordinates, source = 'search') =>
    callbacks.forEach(callback => callback(coordinates, source));

const awaitBrowserGeolocation = () =>
    new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            position =>
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                }),
            error => reject(error),
            {
                maximumAge: 30000,
                timeout: 5000,
            },
        );
    });

const updateResultsByBrowserLocation = () => {
    return awaitBrowserGeolocation()
        .then(result => {
            updateResultsView();
            searchBoxInput.value = '';
            return result;
        })
        .catch(error => {
            console.error(error);
            const GEOLOCATION_ERROR_CODE_MESSAGES = {
                1: 'Der Zugriff auf den Standort wurde abgelehnt. Um diese Funktion zu nutzen, müssen Sie über Ihren Browser den Zugriff auf Ihren Standort gewähren.',
                2: 'Ihre Position konnte nicht ermittelt werden. Eventuell ist der Zugriff auf den Standort über das Betriebssystem deaktiviert, oder Ihr Gerät kann den Standort nicht ermittlen.',
                3: 'Ihre Position kann aktuell nicht bestimmt werden. Bitte versuchen Sie es später erneut.',
            };
            const message =
                GEOLOCATION_ERROR_CODE_MESSAGES[error.code] ||
                'Ein unbekannter Fehler ist aufgetreten.';
            alert(message);
            return null;
        });
};

const updateResultsByBrowserGeolocationAndNotifyCallbacks = () => {
    updateResultsByBrowserLocation().then(result => {
        if (result) executeCallbacks(result, 'geolocation');
    });
};

const forwardGeocode = (query, limit = 5) =>
    new Promise((resolve, reject) => {
        geocodingService
            .forwardGeocode({
                query,
                autocomplete: true,
                fuzzyMatch: true,
                language: ['de'],
                countries: ['de'],
                types: ['postcode', 'place', 'locality', 'neighborhood', 'address', 'poi'],
                limit: limit,
            })
            .send()
            .then(response => {
                const results = response.body.features.map(data => ({
                    latitude: data.center[1],
                    longitude: data.center[0],
                    title: data.place_name,
                }));
                resolve(results);
            })
            .catch(error => reject(error));
    });

const renderSearchResults = results => {
    while (searchResultsList.firstChild) {
        searchResultsList.removeChild(searchResultsList.firstChild);
    }

    results
        .map(result => {
            const item = document.createElement('li');
            const button = document.createElement('button');
            button.dataset.latitude = result.latitude;
            button.dataset.longitude = result.longitude;
            button.textContent = result.title;
            button.classList.add('search-box__result-button');
            item.classList.add('search-box__result-item');
            item.appendChild(button);
            return item;
        })
        .forEach(item => searchResultsList.appendChild(item));
};

const updateResultsView = (config = {}) => {
    const defaultConfig = {
        results: [],
        showNoResults: false,
        showConsentRequired: false,
        showError: false,
    };
    const finalConfig = {
        ...defaultConfig,
        ...config,
    };

    messageNoResults.hidden = !finalConfig.showNoResults;
    messageConsentRequired.hidden = !finalConfig.showConsentRequired;
    messageError.hidden = !finalConfig.showError;

    if (!finalConfig.results.length) {
        return (searchResultsList.hidden = true);
    }

    searchResultsList.hidden = false;
    renderSearchResults(finalConfig.results);
};

const updateResultsBySearchBox = () => {
    if (!window.klaro.getManager().getConsent('mapbox')) return;

    if (searchBoxInput.value.length < 3) {
        return updateResultsView();
    }

    forwardGeocode(searchBoxInput.value)
        .then(results => updateResultsView({ results: results, showNoResults: !results.length }))
        .catch(error => {
            console.error(error);
            updateResultsView({ showError: true });
        });
};

const selectFirstSearchResultWithEnterKey = async event => {
    if (event.code !== 'Enter') return;

    const button = searchResultsList.querySelector(`.${searchBoxResultButtonClass}`);
    if (button) button.click();
};

const handleSearchResultClick = async event => {
    if (!(event.target instanceof HTMLButtonElement)) return;
    const { latitude, longitude } = event.target.dataset;
    searchBoxInput.value = event.target.textContent;
    updateResultsView();
    return { latitude, longitude };
};

const navigateResultsWithArrowKeys = event => {
    if (!['ArrowUp', 'ArrowDown'].includes(event.code)) return;

    event.preventDefault();
    const elements = Array.from(searchResultsList.children);
    if (!elements.length) return;

    const currentIndex = elements.findIndex(
        element =>
            element.querySelector(`.${searchBoxResultButtonClass}`) === document.activeElement,
    );
    let nextIndex;
    switch (event.code) {
        case 'ArrowUp':
            nextIndex = currentIndex - 1;
            if (nextIndex < 0) {
                nextIndex = elements.length - 1;
            }
            break;
        case 'ArrowDown':
            nextIndex = currentIndex + 1;
            if (nextIndex >= elements.length) {
                nextIndex = 0;
            }
            break;
    }

    elements[nextIndex].querySelector(`.${searchBoxResultButtonClass}`).focus();
};

searchBox.addEventListener('keydown', navigateResultsWithArrowKeys);

searchBox.addEventListener('keydown', event => {
    if (event.code !== 'Escape') return;
    updateResultsView();
});

searchBoxInput.addEventListener('input', updateResultsBySearchBox);

searchBoxInput.addEventListener('focus', () => {
    if (window.klaro.getManager().getConsent('mapbox')) return;
    updateResultsView({ showConsentRequired: true });
});

searchBoxInput.addEventListener('click', () => {
    if (!searchBoxInput.value) return;
    searchBoxInput.select();
    searchResultsList.hidden = false;
});

searchBoxInput.addEventListener('keydown', selectFirstSearchResultWithEnterKey);

searchResultsList.addEventListener('click', event => {
    handleSearchResultClick(event).then(result => {
        if (result) executeCallbacks(result, 'search');
    });
});

clientLocationButton.addEventListener('click', updateResultsByBrowserGeolocationAndNotifyCallbacks);

document.addEventListener('click', event => {
    if (searchBox.contains(event.target)) return;
    updateResultsView();
});

consentLink.addEventListener('click', event => {
    event.preventDefault();
    const klaroManager = window.klaro.getManager();
    klaroManager.updateConsent('mapbox', true);
    klaroManager.saveAndApplyConsents();
    updateResultsView();
    updateResultsBySearchBox();
});

window.registerGeolocationCallback = addCallback;
window.requestUpdateByGeolocation = updateResultsByBrowserGeolocationAndNotifyCallbacks;
